import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, Button, Space, Tag, Typography, Card, message, Modal, Descriptions, Tooltip, Alert, Spin, Popconfirm } from 'antd';
import { SearchOutlined, ReloadOutlined, EyeOutlined, SendOutlined, SettingOutlined, CheckCircleOutlined, PauseCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
};

const { Title, Text } = Typography;
const { Option } = Select;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 8px !important;
    font-size: 24px !important;
    font-weight: 600 !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  p {
    color: ${COLORS.textSecondary};
    margin: 0;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 16px;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const PreferenceItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${COLORS.bgPrimary};
  border-radius: 8px;
  margin-bottom: 12px;
  border: 1px solid ${COLORS.border};

  &:last-child {
    margin-bottom: 0;
  }
`;

const PreferenceInfo = styled.div`
  flex: 1;
`;

const PreferenceTitle = styled.div`
  color: ${COLORS.textPrimary};
  font-weight: 500;
  font-size: 14px;
  font-family: 'SF Mono', 'Fira Code', monospace;
`;

const PreferenceDescription = styled.div`
  color: ${COLORS.textSecondary};
  font-size: 12px;
  margin-top: 4px;
`;

interface WebhookEventType {
  key: string;
  value: string;
  description: string;
}

interface WebhookPreferences {
  [key: string]: boolean;
}

interface PreferencesData {
  merchantCompanyId: string;
  merchantCompanyName: string;
  webHookPostUrl: string;
  preferences: WebhookPreferences;
  availableEventTypes: string[];
}

const Webhooks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<any>(null);
  
  // Preferences modal state
  const [preferencesModalVisible, setPreferencesModalVisible] = useState(false);
  const [preferencesData, setPreferencesData] = useState<PreferencesData | null>(null);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState<WebhookEventType[]>([]);

  // Filters
  const [merchantCompanyId, setMerchantCompanyId] = useState(searchParams.get('merchantCompanyId') || '');
  const [eventType, setEventType] = useState(searchParams.get('eventType') || '');
  const [deliveryStatus, setDeliveryStatus] = useState(searchParams.get('deliveryStatus') || '');
  const [source, setSource] = useState(searchParams.get('source') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const fetchEventTypes = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WEBHOOK_EVENT_TYPES);
      if (response.data?.status === 'success' && response.data?.data) {
        setEventTypes(response.data.data.eventTypes || []);
      }
    } catch (error: any) {
      console.error('Error fetching event types:', error);
    }
  }, []);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  const fetchWebhooks = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId: currentPage,
        perPage: pageSize,
        sort: 'DESC',
      };

      if (merchantCompanyId) params.merchantCompanyId = merchantCompanyId;
      if (eventType) params.eventType = eventType;
      if (deliveryStatus) params.deliveryStatus = deliveryStatus;
      if (source) params.source = source;
      if (search) params.search = search;

      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_WEBHOOK_LOGS, { params });
      
      if (response.data?.status === 'success' && response.data?.data) {
        setWebhooks(response.data.data.itemsList || []);
        setTotal(response.data.data.paginator?.itemCount || 0);
      }
    } catch (error: any) {
      message.error('Failed to fetch webhook logs');
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, merchantCompanyId, eventType, deliveryStatus, source, search]);

  useEffect(() => {
    fetchWebhooks();
  }, [fetchWebhooks]);

  const handleFilterChange = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
    
    if (key === 'merchantCompanyId') setMerchantCompanyId(value);
    if (key === 'eventType') setEventType(value);
    if (key === 'deliveryStatus') setDeliveryStatus(value);
    if (key === 'source') setSource(value);
    if (key === 'search') setSearch(value);
    
    setCurrentPage(1);
  };

  const handleRetry = async (webhookLogId: string) => {
    try {
      await apiClient.post(API_ENDPOINTS.ADMIN_RETRY_WEBHOOK.replace('{webhookLogId}', webhookLogId));
      message.success('Webhook retry initiated');
      fetchWebhooks();
    } catch (error: any) {
      message.error('Failed to retry webhook');
      console.error('Error retrying webhook:', error);
    }
  };

  const handleDelete = async (webhookLogId: string) => {
    try {
      await apiClient.delete(API_ENDPOINTS.ADMIN_DELETE_WEBHOOK_LOG.replace('{webhookLogId}', webhookLogId));
      message.success('Webhook log deleted');
      fetchWebhooks();
    } catch (error: any) {
      message.error('Failed to delete webhook log');
      console.error('Error deleting webhook log:', error);
    }
  };

  const handleViewDetails = async (webhookLogId: string) => {
    try {
      const response = await apiClient.get((API_ENDPOINTS.ADMIN_GET_WEBHOOK_LOG || '/api/Administrator/webhooks/{webhookLogId}').replace('{webhookLogId}', webhookLogId));
      if (response.data?.status === 'success' && response.data?.data) {
        setSelectedWebhook(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error: any) {
      message.error('Failed to fetch webhook details');
      console.error('Error fetching webhook details:', error);
    }
  };

  const handleViewPreferences = async (companyId: string) => {
    try {
      setPreferencesLoading(true);
      setPreferencesModalVisible(true);
      const response = await apiClient.get((API_ENDPOINTS.ADMIN_GET_MERCHANT_WEBHOOK_PREFERENCES || '/api/Administrator/webhooks/preferences/{merchantCompanyId}').replace('{merchantCompanyId}', companyId));
      if (response.data?.status === 'success' && response.data?.data) {
        setPreferencesData(response.data.data);
      }
    } catch (error: any) {
      message.error('Failed to fetch merchant preferences');
      console.error('Error fetching preferences:', error);
      setPreferencesModalVisible(false);
    } finally {
      setPreferencesLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    const statusMap: any = {
      PENDING: { color: 'orange', text: 'Pending' },
      SUCCESS: { color: 'green', text: 'Success' },
      FAILED: { color: 'red', text: 'Failed' },
      SKIPPED: { color: 'default', text: 'Skipped' },
    };
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };


  const columns = [
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (text: string) => <Text style={{ color: COLORS.textPrimary }}>{text}</Text>,
    },
    {
      title: 'Merchant Company',
      dataIndex: ['merchantCompany', 'name'],
      key: 'merchantCompany',
      render: (text: string, record: any) => (
        <Space>
          <Text style={{ color: COLORS.textPrimary }}>{text || 'N/A'}</Text>
          {record.merchantCompanyId && (
            <Tooltip title="View webhook preferences">
              <Button
                icon={<SettingOutlined />}
                size="small"
                type="text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewPreferences(record.merchantCompanyId);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: 'Delivery Status',
      dataIndex: 'deliveryStatus',
      key: 'deliveryStatus',
      render: (status: string) => (
        <Tooltip title={status === 'SKIPPED' ? 'Webhook type was disabled by merchant' : undefined}>
          {getStatusTag(status)}
        </Tooltip>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      render: (value: string) => {
        const meta: Record<string, { color: string; help: string }> = {
          MORALIS: { color: 'cyan', help: 'Deposit detected by Moralis Streams and sent by us' },
          WATCHER: { color: 'purple', help: 'Detected by OnchainWatcher, which posted to the merchant directly' },
          ADMIN: { color: 'gold', help: 'Triggered by an administrator' },
          MERCHANT: { color: 'blue', help: 'Triggered by the merchant' },
          SYSTEM: { color: 'default', help: 'Sent by the platform' },
        };
        const entry = meta[value] || meta.SYSTEM;
        return (
          <Tooltip title={entry.help}>
            <Tag color={entry.color}>{value || 'SYSTEM'}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'HTTP Status',
      dataIndex: 'httpStatus',
      key: 'httpStatus',
      render: (status: number) => (
        <Text style={{ color: COLORS.textPrimary }}>{status || 'N/A'}</Text>
      ),
    },
    {
      title: 'Retry Count',
      dataIndex: 'retryCount',
      key: 'retryCount',
      render: (count: number) => <Text style={{ color: COLORS.textPrimary }}>{count || 0}</Text>,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (
        <Text style={{ color: COLORS.textSecondary }}>
          {date ? new Date(date).toLocaleString() : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record.id)}
            size="small"
          >
            View
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={() => handleRetry(record.id)}
            size="small"
            danger={record.deliveryStatus === 'FAILED'}
            type={record.deliveryStatus === 'SUCCESS' ? 'default' : 'primary'}
          >
            Resend
          </Button>
          <Popconfirm
            title={
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Delete webhook log?</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  This permanently deletes this webhook log. This cannot be undone.
                </div>
              </div>
            }
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2}>Webhook Logs</Title>
        <Text type="secondary">View and manage webhook delivery logs</Text>
      </HeaderContainer>

      <FiltersContainer>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap>
            <Input
              placeholder="Search event type or error message"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onBlur={() => fetchWebhooks()}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Delivery Status"
              value={deliveryStatus || undefined}
              onChange={(value) => handleFilterChange('deliveryStatus', value)}
              onBlur={() => fetchWebhooks()}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="PENDING">Pending</Option>
              <Option value="SUCCESS">Success</Option>
              <Option value="FAILED">Failed</Option>
              <Option value="SKIPPED">Skipped</Option>
            </Select>
            <Select
              placeholder="Source"
              value={source || undefined}
              onChange={(value) => handleFilterChange('source', value)}
              onBlur={() => fetchWebhooks()}
              style={{ width: 150 }}
              allowClear
            >
              <Option value="MORALIS">Moralis</Option>
              <Option value="WATCHER">OnchainWatcher</Option>
              <Option value="ADMIN">Admin</Option>
              <Option value="MERCHANT">Merchant</Option>
              <Option value="SYSTEM">System</Option>
            </Select>
            <Input
              placeholder="Event Type"
              value={eventType}
              onChange={(e) => handleFilterChange('eventType', e.target.value)}
              onBlur={() => fetchWebhooks()}
              style={{ width: 200 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchWebhooks}
            >
              Refresh
            </Button>
          </Space>
        </Space>
      </FiltersContainer>

      <Card
        style={{
          background: COLORS.bgSecondary,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <TableScrollWrapper>
          <Table
            columns={columns}
            dataSource={webhooks}
            loading={loading}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total) => `Total ${total} webhooks`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            style={{
              background: 'transparent',
              color: COLORS.textPrimary,
            }}
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      {/* Detail Modal */}
      <Modal
        title="Webhook Log Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button
            key="retry"
            type={selectedWebhook?.deliveryStatus === 'FAILED' ? 'primary' : 'default'}
            danger={selectedWebhook?.deliveryStatus === 'FAILED'}
            icon={<SendOutlined />}
            onClick={() => {
              if (selectedWebhook) {
                handleRetry(selectedWebhook.id);
                setDetailModalVisible(false);
              }
            }}
          >
            Resend Webhook
          </Button>,
          <Popconfirm
            key="delete"
            title={
              <div>
                <div style={{ marginBottom: 4, fontWeight: 500 }}>Delete webhook log?</div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  This permanently deletes this webhook log. This cannot be undone.
                </div>
              </div>
            }
            onConfirm={() => {
              if (selectedWebhook) {
                handleDelete(selectedWebhook.id);
                setDetailModalVisible(false);
              }
            }}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
        style={{ top: 20 }}
      >
        {selectedWebhook && (
          <Descriptions
            column={1}
            bordered
            labelStyle={{
              background: COLORS.bgPrimary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
            }}
            contentStyle={{
              background: COLORS.bgSecondary,
              color: COLORS.textPrimary,
              borderColor: COLORS.border,
            }}
          >
            <Descriptions.Item label="Event Type">{selectedWebhook.eventType}</Descriptions.Item>
            <Descriptions.Item label="Merchant Company">
              <Space>
                {selectedWebhook.merchantCompany?.name || 'N/A'}
                {selectedWebhook.merchantCompanyId && (
                  <Button
                    type="link"
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={() => {
                      setDetailModalVisible(false);
                      handleViewPreferences(selectedWebhook.merchantCompanyId);
                    }}
                  >
                    View Preferences
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Status">
              {getStatusTag(selectedWebhook.deliveryStatus)}
              {selectedWebhook.deliveryStatus === 'SKIPPED' && (
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  (Webhook type was disabled by merchant)
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="HTTP Status">{selectedWebhook.httpStatus || 'N/A'}</Descriptions.Item>
            <Descriptions.Item label="Retry Count">{selectedWebhook.retryCount || 0}</Descriptions.Item>
            <Descriptions.Item label="Webhook URL">{selectedWebhook.webhookUrl}</Descriptions.Item>
            <Descriptions.Item label="Error Message">
              {selectedWebhook.errorMessage || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Response Body">
              <pre style={{ 
                background: COLORS.bgPrimary, 
                padding: 8, 
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
              }}>
                {selectedWebhook.responseBody || 'N/A'}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="Payload">
              <pre style={{ 
                background: COLORS.bgPrimary, 
                padding: 8, 
                borderRadius: 4,
                overflow: 'auto',
                maxHeight: 200,
              }}>
                {JSON.stringify(selectedWebhook.payload, null, 2)}
              </pre>
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {new Date(selectedWebhook.createdAt).toLocaleString()}
            </Descriptions.Item>
            <Descriptions.Item label="Last Attempt At">
              {selectedWebhook.lastAttemptAt ? new Date(selectedWebhook.lastAttemptAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Delivered At">
              {selectedWebhook.deliveredAt ? new Date(selectedWebhook.deliveredAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Merchant Webhook Preferences Modal */}
      <Modal
        title={
          <Space>
            <SettingOutlined style={{ color: COLORS.accent }} />
            <span>Merchant Webhook Preferences</span>
          </Space>
        }
        open={preferencesModalVisible}
        onCancel={() => {
          setPreferencesModalVisible(false);
          setPreferencesData(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setPreferencesModalVisible(false);
            setPreferencesData(null);
          }}>
            Close
          </Button>,
        ]}
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary }}
      >
        {preferencesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: COLORS.textSecondary }}>Loading preferences...</div>
          </div>
        ) : preferencesData ? (
          <>
            <Alert
              message={
                <Space direction="vertical" size={0}>
                  <Text strong style={{ color: COLORS.textPrimary }}>
                    {preferencesData.merchantCompanyName}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Webhook URL: {preferencesData.webHookPostUrl || 'Not configured'}
                  </Text>
                </Space>
              }
              type="info"
              style={{ marginBottom: 16, background: COLORS.bgPrimary, border: `1px solid ${COLORS.border}` }}
            />
            
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ color: COLORS.textPrimary, display: 'block', marginBottom: 12 }}>
                Event Type Preferences (Read Only)
              </Text>
              {eventTypes.map((et) => (
                <PreferenceItem key={et.value}>
                  <PreferenceInfo>
                    <PreferenceTitle>
                      {preferencesData.preferences[et.value] !== false ? (
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      ) : (
                        <PauseCircleOutlined style={{ color: COLORS.textSecondary, marginRight: 8 }} />
                      )}
                      {et.value}
                    </PreferenceTitle>
                    <PreferenceDescription>{et.description}</PreferenceDescription>
                  </PreferenceInfo>
                  <Tag color={preferencesData.preferences[et.value] !== false ? 'green' : 'default'}>
                    {preferencesData.preferences[et.value] !== false ? 'Enabled' : 'Disabled'}
                  </Tag>
                </PreferenceItem>
              ))}
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>
            No preferences data available
          </div>
        )}
      </Modal>
    </PageContainer>
  );
};

export default Webhooks;
